import { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { useI18n } from '../context/I18nContext';
import { API_BASE } from '../config';

const CATEGORY_ICONS = {
  friend: '👥',
  colleague: '💼',
  superior: '⭐'
};

export default function ContactManager() {
  const { setShowContactManager, isAdmin } = useApp();
  const { t } = useI18n();
  const [contacts, setContacts] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('colleague');

  const token = localStorage.getItem('cogency_token');

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setContacts(Array.isArray(data) ? data : []);
    } catch {
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchAllUsers = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await fetch(`${API_BASE}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setAllUsers(Array.isArray(data) ? data.filter(u => u.role !== 'admin' && u.role !== 'pending') : []);
    } catch {
      setAllUsers([]);
    }
  }, [isAdmin, token]);

  useEffect(() => {
    fetchContacts();
    fetchAllUsers();
  }, [fetchContacts, fetchAllUsers]);

  const handleAdd = async () => {
    if (!selectedUserId) return;
    try {
      await fetch(`${API_BASE}/api/contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ contactId: selectedUserId, category: selectedCategory })
      });
      setSelectedUserId('');
      fetchContacts();
    } catch { /* ignore */ }
  };

  const handleUpdateCategory = async (contactId, category) => {
    try {
      await fetch(`${API_BASE}/api/contacts/${contactId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ category })
      });
      fetchContacts();
    } catch { /* ignore */ }
  };

  const handleRemove = async (contactId) => {
    if (!confirm(t('confirmDeleteContact'))) return;
    try {
      await fetch(`${API_BASE}/api/contacts/${contactId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchContacts();
    } catch { /* ignore */ }
  };

  const existingContactIds = new Set(contacts.map(c => c.contactId));
  const availableUsers = allUsers.filter(u => !existingContactIds.has(u.id));

  return (
    <div className="modal-overlay show" onClick={e => e.target === e.currentTarget && setShowContactManager(false)}>
      <div className="modal modal-wide">
        <div className="modal-header">
          <h2>👥 {t('contactManager')}</h2>
          <button className="modal-close" onClick={() => setShowContactManager(false)}>&times;</button>
        </div>
        <div className="modal-body">
          {isAdmin && availableUsers.length > 0 && (
            <div className="contact-add-row">
              <select value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}>
                <option value="">{t('selectUser')}</option>
                {availableUsers.map(u => (
                  <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                ))}
              </select>
              <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)}>
                <option value="friend">{t('friend')}</option>
                <option value="colleague">{t('colleague')}</option>
                <option value="superior">{t('superior')}</option>
              </select>
              <button className="btn-primary btn-small" onClick={handleAdd} disabled={!selectedUserId}>
                {t('add')}
              </button>
            </div>
          )}

          {loading ? (
            <p className="empty-state">{t('loading')}...</p>
          ) : contacts.length === 0 ? (
            <p className="empty-state">{t('noContactsYet')}</p>
          ) : (
            <div className="contact-list">
              {contacts.map(c => (
                <div key={c.contactId} className="contact-item">
                  <span className="contact-icon">{CATEGORY_ICONS[c.category] || '💼'}</span>
                  <div className="contact-info">
                    <div className="contact-name">{c.name}</div>
                    <div className="contact-email">{c.email}</div>
                  </div>
                  <select
                    className="contact-category-select"
                    value={c.category}
                    onChange={e => handleUpdateCategory(c.contactId, e.target.value)}
                  >
                    <option value="friend">{t('friend')}</option>
                    <option value="colleague">{t('colleague')}</option>
                    <option value="superior">{t('superior')}</option>
                  </select>
                  <button className="btn-danger btn-small" onClick={() => handleRemove(c.contactId)}>
                    🗑️
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
